import React, { useCallback, useEffect, useState } from 'react';
import WizardStepLayout from '../WizardStepLayout';
import SubmissionUploadProgress from '../../SubmissionUploadProgress';
import { WIZARD_ACTIONS } from '../constants';
import { VISIBILITY, SHAPE_TYPE } from '../../utils/constants';
import { useAuth } from '../../authContext';
import { useProject } from '../../ProjectContext';

function getDecadeLabel(year) {
  if (!year) return '';
  const num = parseInt(year, 10);
  if (isNaN(num)) return year;
  if (num === 1760) return 'Pre-1770';
  if (num === 2030) return 'Vision';
  const decade = Math.floor(num / 10) * 10;
  return `${decade}s`;
}

const VISIBILITY_OPTIONS = [
  {
    value: VISIBILITY.PUBLIC,
    label: 'Public',
    desc: 'Anyone visiting the map can see this place.',
  },
  {
    value: VISIBILITY.PROJECT,
    label: 'Members only',
    desc: 'Only people with member access to this map can see it.',
  },
  {
    value: VISIBILITY.PRIVATE,
    label: 'Private',
    desc: 'Only you can see this place.',
    requiresAuth: true,
  },
];

const ReviewStep = ({
  state,
  dispatch,
  fileCount,
  files,
  onBack,
  onSubmit,
  progress,
  isSubmitting,
  error,
  isExhibitionMode,
}) => {
  const { user } = useAuth();
  const { project } = useProject();
  const [imageThumbUrls, setImageThumbUrls] = useState([]);

  // Generate thumbnails for uploaded image files so the user can confirm
  // what they're about to submit. Non-image files are excluded.
  useEffect(() => {
    if (!files) {
      setImageThumbUrls([]);
      return undefined;
    }
    const urls = files
      .filter((entry) => entry.file.type.startsWith('image/'))
      .map((entry) => ({
        url: URL.createObjectURL(entry.file),
        name: entry.file.name,
        caption: entry.caption,
      }));
    setImageThumbUrls(urls);
    return () => {
      urls.forEach((u) => URL.revokeObjectURL(u.url));
    };
  }, [files]);

  const handleVisibilityChange = useCallback(
    (value) => {
      dispatch({ type: WIZARD_ACTIONS.SET_VISIBILITY, payload: value });
    },
    [dispatch],
  );

  const isSiteTypeCity = project?.site_type === 'city';
  const showContactFields = !user || isExhibitionMode;

  const handleContactChange = useCallback(
    (field, value) => {
      dispatch({ type: field, payload: value });
    },
    [dispatch],
  );

  const submitDisabled =
    isSubmitting ||
    (showContactFields &&
      (!state.contFirstName?.trim() ||
        !state.contLastName?.trim() ||
        !state.contEmail?.trim()));

  return (
    <WizardStepLayout
      stepNumber={6}
      onBack={onBack}
      showBack
      onNext={onSubmit}
      nextLabel={isSubmitting ? 'Submitting...' : 'Submit Entry'}
      nextDisabled={submitDisabled}
      nextPrimary
    >
      {showContactFields && (
        <div className="wizard__contact-section">
          <p className="wizard__contact-heading">Please fill in your information below.</p>
          <input
            className="wizard__input"
            type="text"
            placeholder="First Name *"
            value={state.contFirstName}
            onChange={(e) => handleContactChange(WIZARD_ACTIONS.SET_CONT_FIRST_NAME, e.target.value)}
          />
          <input
            className="wizard__input"
            type="text"
            placeholder="Last Name *"
            value={state.contLastName}
            onChange={(e) => handleContactChange(WIZARD_ACTIONS.SET_CONT_LAST_NAME, e.target.value)}
          />
          <input
            className="wizard__input"
            type="email"
            placeholder="Email *"
            value={state.contEmail}
            onChange={(e) => handleContactChange(WIZARD_ACTIONS.SET_CONT_EMAIL, e.target.value)}
          />
        </div>
      )}

      <div>
        <div className="wizard__review-row">
          <span className="wizard__review-label">Location</span>
          <span className="wizard__review-value">
            {state.shapeType === SHAPE_TYPE.POLYGON
              ? `Drawn region (${state.polygonCoordinates?.length || 0} points)`
              : (state.address || 'Map pin')}
          </span>
        </div>
        <div className="wizard__review-row">
          <span className="wizard__review-label">Era</span>
          <span className="wizard__review-value">{getDecadeLabel(state.year)}</span>
        </div>
        <div className="wizard__review-row">
          <span className="wizard__review-label">Place name</span>
          <span className="wizard__review-value">{state.placeName}</span>
        </div>
        <div className="wizard__review-row">
          <span className="wizard__review-label">Story</span>
          <span className="wizard__review-value">
            {state.storyText?.trim() ? 'Added' : 'Not added'}
          </span>
        </div>
        {fileCount > 0 && (
          <div className="wizard__review-row">
            <span className="wizard__review-label">Files</span>
            <span className="wizard__review-value">
              {fileCount} file{fileCount > 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>

      {imageThumbUrls.length > 0 && (
        <div className="wizard__review-thumbs">
          {imageThumbUrls.map((thumb) => (
            <figure key={thumb.url} className="wizard__review-thumb-block">
              <img
                className="wizard__review-thumb"
                src={thumb.url}
                alt={thumb.name}
              />
              {thumb.caption ? (
                <figcaption className="wizard__review-thumb-caption">
                  {thumb.caption}
                </figcaption>
              ) : null}
            </figure>
          ))}
        </div>
      )}

      <div className="wizard__visibility-box">
        <p className="wizard__visibility-title">WHO CAN SEE THIS?</p>
        {VISIBILITY_OPTIONS.map((opt) => {
          if (isSiteTypeCity && opt.value === VISIBILITY.PROJECT) return null;
          const disabled = opt.requiresAuth && !user;
          return (
            <label
              key={opt.value}
              className={
                'wizard__visibility-option' +
                (disabled ? ' wizard__visibility-option--disabled' : '')
              }
            >
              <input
                type="radio"
                name="wizard-visibility"
                value={opt.value}
                checked={state.visibility === opt.value}
                disabled={disabled}
                onChange={() => handleVisibilityChange(opt.value)}
              />
              <div>
                <p className="wizard__visibility-label">{opt.label}</p>
                <p className="wizard__visibility-desc">
                  {disabled ? 'Sign in to use this option.' : opt.desc}
                </p>
              </div>
            </label>
          );
        })}
      </div>

      <SubmissionUploadProgress progress={progress} />

      {error && <p className="wizard__error">{error}</p>}
    </WizardStepLayout>
  );
};

export default React.memo(ReviewStep);
